import React, { useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { motion, useInView, useScroll, useTransform } from "framer-motion";

/* ═══════════════════════════════════════════════════
   DATA — Work Projects
   ═══════════════════════════════════════════════════ */

interface WorkProject {
  title: string;
  headline: string;
  subtitle: string;
  description: string;
  bullets: string[];
  impact?: string;
  tech: string[];
  live?: string;
  screenshot: string;
  screenshotAlt: string;
  annotation?: string;
  side: "jobseeker" | "recruiter";
}

const WORK_PROJECTS: WorkProject[] = [
  {
    title: "Student Ambassador Programme",
    headline: "Campus Squad Programme Launches With Animated Quiz Flow",
    subtitle: "Naukri Campus Squad — Jobseeker Side",
    description:
      "A gamified ambassador programme enabling students to represent Naukri Campus at their colleges. Features a complete interactive quiz flow with smooth 60fps animations built using react-spring with GSAP proof-of-concept.",
    bullets: [
      "Built complex animations using react-spring with GSAP proof-of-concept",
      "Developed complete quiz flow with modular coding standards across sapFlow",
      "Maintained animation performance at 60fps across devices",
    ],
    tech: ["React", "react-spring", "GSAP", "TypeScript"],
    live: "https://www.naukri.com/campus/ambassador-program?src=gnbParticipate",
    screenshot: "/projects/wp-1.png",
    screenshotAlt: "Naukri Campus Squad Ambassador Programme",
    annotation: "smooth 60fps!",
    side: "jobseeker",
  },
  {
    title: "Interview Experience Chatbot",
    headline: "AI-Powered Chatbot Revolutionises Interview Preparation",
    subtitle: "Real Interview Experiences — Powered by AI/LLM",
    description:
      "An intelligent chatbot that serves real interview experiences from top companies. Users can query any company, role, or round type and get AI-curated responses drawn from thousands of actual interview experiences.",
    bullets: [
      "Built AI/LLM powered interview experiences chatbot from scratch",
      "Integrated Coding Ninjas & Naukri360 interview-experiences data sources",
      "Added chat history, recommendations, and feedback features",
      "Designed custom interview experience cards and question cards",
    ],
    tech: ["React", "LLM/AI", "TypeScript", "Chat UI"],
    live: "https://www.naukri.com/campus/interview-experiences",
    screenshot: "/projects/wp-2.png",
    screenshotAlt: "AI Interview Experience Chatbot Interface",
    annotation: "AI magic",
    side: "jobseeker",
  },
  {
    title: "AINCAT & Contests",
    headline:
      "All India Aptitude Test Draws 60K+ Aspirants; Contests Platform Sees Record Traffic",
    subtitle: "AINCAT + Contests Listing — Naukri Campus",
    description:
      "Built the All India NCAT page and the contests listing platform powering India's largest career aptitude test. The contests page serves as the central hub for hackathons, quizzes, coding contests, and hiring challenges across the nation.",
    bullets: [
      "Built AINCAT page powering 20K+ users with pre/post enrollment management",
      "Drove performance optimization across 10+ high-traffic pages",
      "Improved Core Web Vitals — INP down 38%, CLS down 95%",
      "Migrated 7+ React components to React Native and Flutter",
    ],
    impact: "INP ↓38% · CLS ↓95% · 60K+ enrolled",
    tech: ["React", "React Native", "Flutter", "Web Vitals"],
    live: "https://www.naukri.com/campus/contests?src=gnbParticipate",
    screenshot: "/projects/wp-3.png",
    screenshotAlt: "AINCAT Aptitude Test and Contests Platform",
    annotation: "CLS ↓95%!",
    side: "jobseeker",
  },
  {
    title: "Myntra Quiz Challenge",
    headline:
      "Get Real With Myntra: 45-Day Quiz Draws 1.4 Lakh Participants",
    subtitle: "Myntra x Naukri Campus — Gamification",
    description:
      "A 45-day daily quiz challenge partnered with Myntra, offering tiered rewards from vouchers worth ₹5,000 to Myntra goodies. The campaign achieved viral enrollment numbers through gamified engagement mechanics and daily participation streaks.",
    bullets: [
      "Built the complete 45-day quiz challenge experience end-to-end",
      "Implemented tiered reward system (Rank 1-3: ₹5K, Rank 4-15: ₹1K, etc.)",
      "Achieved 1.4L+ total enrollments through gamified engagement",
    ],
    impact: "1.4L+ enrolled · 45-day campaign · 4 reward tiers",
    tech: ["React", "TypeScript", "Gamification"],
    live: "https://www.naukri.com/campus/contests/get-real-with-myntra-quiz",
    screenshot: "/projects/wp-4.png",
    screenshotAlt: "Get Real With Myntra Quiz Challenge",
    annotation: "1.4 Lakh!",
    side: "jobseeker",
  },
  {
    title: "Campus Pulse",
    headline: "Campus Pulse Dashboard Maps 8,700+ Colleges Across India",
    subtitle: "Recruiter Analytics — Full Ownership",
    description:
      "A comprehensive college analytics dashboard providing recruiters with granular insights across 8.7K colleges. Features location distribution by zones, states and cities, course specialization breakdowns, and hiring trend analytics with filterable demographic data.",
    bullets: [
      "Led recruiter-side frontend architecture for Naukri Campus",
      "Built analytics dashboard covering 8.7K colleges with location distribution",
      "Implemented course specialization and hiring trends visualizations",
      "Enabled scalable hiring workflows for 10K+ college freshers",
    ],
    impact: "8.7K+ colleges · 21.2L students · 4 zone analytics",
    tech: ["React", "TypeScript", "D3.js", "Analytics"],
    screenshot: "/projects/wp-5.png",
    screenshotAlt: "Campus Pulse College Analytics Dashboard",
    annotation: "full ownership",
    side: "recruiter",
  },
  {
    title: "Branding — Contests",
    headline:
      "Recruiter Contest Platform Powers Participation Funnels For 200K+ Users",
    subtitle: "Contest Management — Recruiter Branding",
    description:
      "A recruiter-facing contest management platform enabling creation, management, and analysis of engagement campaigns. Features participation funnels, top talent identification, and detailed analytics dashboards for tracking contest performance across colleges.",
    bullets: [
      "Built contest management with participation funnels and top talent identification",
      "Architected and standardized 6+ reusable React patterns (feature-based modular structure)",
      "Developed production-grade features impacting 200K+ users",
    ],
    impact: "200K+ users · 6+ reusable patterns",
    tech: ["React", "TypeScript", "Custom Hooks", "Architecture"],
    screenshot: "/projects/wp-6.png",
    screenshotAlt: "Recruiter Contest Management Dashboard",
    annotation: "200K+ users",
    side: "recruiter",
  },
  {
    title: "Branding — Webinars",
    headline:
      "Webinar Platform Delivers Demographic Insights & Recording Access To Recruiters",
    subtitle: "Webinar Hosting — Recruiter Branding",
    description:
      "A full-featured webinar hosting platform for recruiters to conduct live sessions, track attendance demographics, and provide recording access. Includes registration management, real-time participant analytics, and post-webinar engagement tracking.",
    bullets: [
      "Created webinar platform with demographic insights and recording access",
      "Built registration management with real-time participant analytics",
      "Implemented post-webinar engagement tracking and follow-ups",
    ],
    impact: "Live sessions · Demographic analytics · Recordings",
    tech: ["React", "TypeScript", "WebSocket", "Analytics"],
    screenshot: "/projects/wp-6b.png",
    screenshotAlt: "Recruiter Webinar Platform",
    annotation: "live sessions",
    side: "recruiter",
  },
  {
    title: "Branding — RoleSense",
    headline:
      "RoleSense Drives Targeted Talent Engagement With Impressions Tracking",
    subtitle: "RoleSense — Recruiter Branding",
    description:
      "A targeted talent engagement tool enabling recruiters to reach the right candidates through role-based content. Features impressions tracking, engagement analytics, and smart targeting based on student profiles and career interests.",
    bullets: [
      "Built RoleSense for targeted talent engagement and impressions tracking",
      "Implemented smart targeting based on student profiles and career interests",
      "Developed engagement analytics dashboard for recruiters",
    ],
    impact: "Targeted engagement · Impressions tracking",
    tech: ["React", "TypeScript", "Analytics", "Custom Hooks"],
    screenshot: "/projects/wp-6c.png",
    screenshotAlt: "RoleSense Talent Engagement Platform",
    annotation: "smart targeting",
    side: "recruiter",
  },
  {
    title: "Resdex Search",
    headline:
      "Resdex Resume Search Engine Automates 10K+ Recruiter Transactions",
    subtitle: "Resume Search & Subscriptions — Recruiter Side",
    description:
      "Built the recruiter resume search (Resdex) solution enabling recruiters to find the right candidates with powerful filters. The system automates subscription management, quota distribution, and refund processing across multiple products.",
    bullets: [
      "Built Resdex recruiter resume search with filters (Year, CGPA, Skills)",
      "Created recruiter subscription and quota-distribution APIs",
      "Automated refunds and quota adjustments across 3+ products",
    ],
    impact: "10K+ transactions · Multi-product quotas",
    tech: ["React", "TypeScript", "Node.js", "API Design"],
    screenshot: "/projects/wp-7.png",
    screenshotAlt: "Resdex Resume Search Engine",
    annotation: "10K+ transactions",
    side: "recruiter",
  },
  {
    title: "Job Posting & Drafts",
    headline:
      "Job Posting Workflow With Drafts & Screening Powers 10K+ Responses Per Campaign",
    subtitle: "Job Management · Drafts · Screening — Recruiter Side",
    description:
      "A streamlined job posting workflow with draft functionality, clone and pre-fill capabilities. Enhanced with recruiter-defined screening questions and Excel export for processing thousands of applicant responses per campaign.",
    bullets: [
      "Implemented job posting with draft, clone, and pre-fill from existing jobs",
      "Enhanced pipeline with recruiter-defined screening questions",
      "Built Excel export for 10K+ applicant responses per campaign",
    ],
    impact: "10K+ responses/campaign · Draft & clone support",
    tech: ["React", "TypeScript", "Node.js", "Excel Export"],
    screenshot: "/projects/wp-7b.png",
    screenshotAlt: "Job Posting and Draft Functionality",
    annotation: "draft & clone",
    side: "recruiter",
  },
];

/* ═══════════════════════════════════════════════════
   DATA — Personal Projects
   ═══════════════════════════════════════════════════ */

interface PersonalProject {
  title: string;
  description: string;
  tech: string[];
  github: string;
  live?: string;
  accent: string;
}

const PERSONAL_PROJECTS: PersonalProject[] = [
  {
    title: "Productive Weekday",
    description:
      "Counts total GitHub contributions and visualizes data as a racebar graph using d3.js.",
    tech: ["React", "Redux", "MongoDB", "d3.js"],
    github: "https://github.com/jugshaurya/productive-weekday",
    live: "https://productive-weekday.netlify.app/",
    accent: "#f59e0b",
  },
  {
    title: "Trello Clone",
    description:
      "Kanban-style board with lists, cards, drag & drop, and user auth.",
    tech: ["React", "Redux", "Node", "Express"],
    github: "https://github.com/jugshaurya/trello-clone",
    live: "https://clone-trello.vercel.app/",
    accent: "#3b82f6",
  },
  {
    title: "Closet Goodies",
    description: "E-commerce fashion web app with categories and clothing types.",
    tech: ["React", "Redux", "Firebase", "Sass"],
    github: "https://github.com/jugshaurya/closetgoodies",
    live: "https://closet-goodies.firebaseapp.com/",
    accent: "#ec4899",
  },
  {
    title: "Covid19 Visualizer",
    description: "Visualizes COVID-19 impact over the world map with Mapbox GL.",
    tech: ["React", "Mapbox GL", "GeoJSON"],
    github: "https://github.com/jugshaurya/covid19app",
    live: "https://covid19shau.vercel.app/",
    accent: "#22c55e",
  },
  {
    title: "Shaurya's Notes",
    description:
      "Docusaurus knowledge base with 25+ topics, 56 auto-generated blog posts.",
    tech: ["Docusaurus", "React", "MDX", "Jupyter"],
    github: "https://github.com/jugshaurya/Notes",
    live: "https://notes.jugshaurya.vercel.app/",
    accent: "#2563eb",
  },
  {
    title: "Mario Bros Lvl 1",
    description: "Classic Super Mario Bros Level 1-1 built from scratch.",
    tech: ["JavaScript", "Phaser v3", "Webpack"],
    github: "https://github.com/jugshaurya/mario",
    live: "https://mario-shaurya.vercel.app",
    accent: "#ef4444",
  },
  {
    title: "URL Shortener",
    description: "Creates short links by giving long URLs a unique short name.",
    tech: ["Node", "Express", "MongoDB"],
    github: "https://github.com/jugshaurya/shortener",
    live: "https://little.vercel.app/",
    accent: "#8b5cf6",
  },
  {
    title: "Merge Videos",
    description: "Merges all videos inside a directory into a single video.",
    tech: ["Node.js"],
    github: "https://github.com/jugshaurya/merge-videos",
    accent: "#06b6d4",
  },
  {
    title: "Shaurya Showcase",
    description:
      "This HP themed portfolio — React + Three.js with magical UI.",
    tech: ["React", "TypeScript", "Three.js", "GSAP"],
    github: "https://github.com/jugshaurya/shaurya_showcase",
    live: "https://shaurya.vercel.app/",
    accent: "#a78bfa",
  },
];

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */

const Projects: React.FC = () => {
  return (
    <Page id="projects">
      <Masthead />
      <WorkProjectsCarousel />

      {/* Personal Projects */}
      <PersonalSection>
        <PDivider>
          <PDivLine />
          <PDivDot>&#10040;</PDivDot>
          <PDivLine />
        </PDivider>
        <PSectionHead>
          <PSectionLabel>Classifieds</PSectionLabel>
          <PSectionTitle>Experimental Spells: Personal Projects</PSectionTitle>
          <PSectionSub>
            Side quests, passion projects, and weekend experiments
          </PSectionSub>
        </PSectionHead>
        <ClassifiedGrid>
          {PERSONAL_PROJECTS.map((p, i) => (
            <ClassifiedCard key={p.title} project={p} index={i} />
          ))}
        </ClassifiedGrid>
      </PersonalSection>

      <ProphetFooter />
    </Page>
  );
};

export default Projects;

/* ═══════════════════════════════════════════════════
   MASTHEAD (dark, full-height intro)
   ═══════════════════════════════════════════════════ */

const Masthead: React.FC = () => {
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const y = useTransform(scrollY, [0, 400], [0, -60]);

  return (
    <MastheadWrap>
      <motion.div style={{ opacity, y }}>
        <MastOrnament>&#10038; &#10038; &#10038;</MastOrnament>
        <MastRow>
          <MastEdition>Est. 2022</MastEdition>
          <MastName>The Daily Prophet</MastName>
          <MastEdition>Portfolio Edition</MastEdition>
        </MastRow>
        <MastRule />
        <MastMeta>
          <span>Noida, India</span>
          <MastDot>&#10040;</MastDot>
          <span>
            {new Date().toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <MastDot>&#10040;</MastDot>
          <span>Shaurya Singhal</span>
        </MastMeta>
        <MastTagline>
          "I solemnly swear that I am up to no good"
        </MastTagline>
        <MastRule />
      </motion.div>

      <ScrollHint
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <ScrollQuill
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          &#9998;
        </ScrollQuill>
        <ScrollLbl>Scroll to read</ScrollLbl>
      </ScrollHint>
    </MastheadWrap>
  );
};

/* ═══════════════════════════════════════════════════
   CAROUSEL — single sticky 100vh viewport
   ═══════════════════════════════════════════════════ */

const WorkProjectsCarousel: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const N = WORK_PROJECTS.length;

  return (
    <CarouselOuter ref={containerRef} style={{ height: `${N * 100}vh` }}>
      <CarouselSticky>
        {WORK_PROJECTS.map((project, i) => (
          <NewspaperSlide
            key={project.title}
            project={project}
            index={i}
            total={N}
            progress={scrollYProgress}
          />
        ))}
        {/* Progress dots + titles on the right */}
        <DotsCol>
          {WORK_PROJECTS.map((p, i) => (
            <DotRow key={p.title}>
              <DotItem index={i} total={N} progress={scrollYProgress} />
              <DotLabel index={i} total={N} progress={scrollYProgress} title={p.title} />
            </DotRow>
          ))}
        </DotsCol>
      </CarouselSticky>
    </CarouselOuter>
  );
};

/* ═══════════════════════════════════════════════════
   NEWSPAPER SLIDE — card-stack effect
   Current card: scale 0.9, y -30 (pushed back)
   Next card: slides up from 100% below at full scale
   Later cards stack on top of earlier ones (z-index)
   ═══════════════════════════════════════════════════ */

const NewspaperSlide: React.FC<{
  project: WorkProject;
  index: number;
  total: number;
  progress: ReturnType<typeof useScroll>["scrollYProgress"];
}> = ({ project, index, total, progress }) => {
  const [preview, setPreview] = useState(false);
  const [tab, setTab] = useState<"overview" | "details" | "impact">("overview");
  const openPreview = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(true);
    document.body.style.overflow = "hidden";
  }, []);
  const closePreview = useCallback(() => {
    setPreview(false);
    document.body.style.overflow = "";
  }, []);
  const T = total - 1; // number of transitions

  // Build the full y trajectory for this slide
  let yInput: number[];
  let yOutput: number[];
  let scaleInput: number[];
  let scaleOutput: number[];

  const WH = typeof window !== "undefined" ? window.innerHeight : 900;

  if (index === 0) {
    // First slide: starts visible, then recedes
    yInput = [0, 1 / T];
    yOutput = [0, -30];
    scaleInput = [0, 1 / T];
    scaleOutput = [1, 0.9];
  } else if (index === total - 1) {
    // Last slide: enters from bottom, never recedes
    yInput = [(index - 1) / T, index / T];
    yOutput = [WH, 0];
    scaleInput = [0, 1];
    scaleOutput = [1, 1];
  } else {
    // Middle slides: enter from bottom, then recede
    yInput = [(index - 1) / T, index / T, (index + 1) / T];
    yOutput = [WH, 0, -30];
    scaleInput = [(index - 1) / T, index / T, (index + 1) / T];
    scaleOutput = [1, 1, 0.9];
  }

  const y = useTransform(progress, yInput, yOutput);
  const scale = useTransform(progress, scaleInput, scaleOutput);

  const sideLabel =
    project.side === "jobseeker" ? "Jobseeker Side" : "Recruiter Side";

  return (
    <SlideAbsolute style={{ y, scale, zIndex: index + 1 }}>
      <SpellPage>
        {/* Header row: number + badge */}
        <SpellHeaderRow>
          <SpellNum>Spell {index + 1} of {total}</SpellNum>
          <SpellClassBadge $side={project.side}>
            {project.side === "recruiter" ? "⚡ Ownership" : "✦ Jobseeker"}
          </SpellClassBadge>
        </SpellHeaderRow>

        {/* Spell name */}
        <SpellName>{project.title}</SpellName>
        <SpellLatin>{project.headline}</SpellLatin>

        {/* Illustration — hidden for recruiter projects */}
        {project.side !== "recruiter" && (
          <SpellIllustration onClick={openPreview} style={{ cursor: "zoom-in" }}>
            <SpellImg src={project.screenshot} alt={project.screenshotAlt} loading="lazy" />
            {project.annotation && <SpellAnnotation>{project.annotation}</SpellAnnotation>}
          </SpellIllustration>
        )}

        {/* Tabs below image */}
        <SpellTabs>
          <SpellTab $active={tab === "overview"} onClick={() => setTab("overview")}>Effect</SpellTab>
          <SpellTab $active={tab === "details"} onClick={() => setTab("details")}>Instructions</SpellTab>
          <SpellTab $active={tab === "impact"} onClick={() => setTab("impact")}>Impact</SpellTab>
        </SpellTabs>

        <SpellTabContent>
          {tab === "overview" && (
            <>
              <SpellText>{project.description}</SpellText>
              <SpellIngredients style={{ marginTop: "0.8rem" }}>
                {project.tech.map((t) => (
                  <SpellIngredient key={t}>{t}</SpellIngredient>
                ))}
              </SpellIngredients>
            </>
          )}

          {tab === "details" && (
            <>
              {project.bullets.map((b, bi) => (
                <SpellStep key={bi}>
                  <SpellStepNum>{bi + 1}.</SpellStepNum> {b}
                </SpellStep>
              ))}
            </>
          )}

          {tab === "impact" && (
            <>
              {project.impact && (
                <SpellPower>
                  <SpellLabel>Power Output</SpellLabel>
                  <SpellPowerText>{project.impact}</SpellPowerText>
                </SpellPower>
              )}
              <SpellIngredients>
                {project.tech.map((t) => (
                  <SpellIngredient key={t}>{t}</SpellIngredient>
                ))}
              </SpellIngredients>
              <SpellByline>Authored by Shaurya Singhal</SpellByline>
            </>
          )}
        </SpellTabContent>

        {/* Live link sits outside the tabs — it was previously only rendered
            on the "details" tab, which made it look like the URL was missing. */}
        {project.live && (
          <SpellVisit href={project.live} target="_blank" rel="noopener noreferrer">
            See Live &#10132;
          </SpellVisit>
        )}
      </SpellPage>

      {preview && createPortal(
        <LightboxOverlay
          onClick={closePreview}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <LightboxClose onClick={closePreview}>&times;</LightboxClose>
          <LightboxImg
            src={project.screenshot}
            alt={project.screenshotAlt}
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
          <LightboxCaption>{project.title}</LightboxCaption>
        </LightboxOverlay>,
        document.body
      )}
    </SlideAbsolute>
  );
};

/* ── Progress dot (uses functional useTransform to avoid offset issues) ── */
const DotItem: React.FC<{
  index: number;
  total: number;
  progress: ReturnType<typeof useScroll>["scrollYProgress"];
}> = ({ index, total, progress }) => {
  const T = Math.max(total - 1, 1);
  const activeAt = index / T;
  const halfStep = 0.5 / T;

  // Use functional transform to avoid offset clamping issues
  const dotOpacity = useTransform(progress, (p) => {
    const dist = Math.abs(p - activeAt) / halfStep;
    return Math.max(0.2, 1 - dist * 0.8);
  });
  const dotScale = useTransform(progress, (p) => {
    const dist = Math.abs(p - activeAt) / halfStep;
    return Math.max(1, 1.8 - dist * 0.8);
  });
  return <DotCircle style={{ opacity: dotOpacity, scale: dotScale }} />;
};

/* ── Dot label showing project title next to dot ── */
const DotLabel: React.FC<{
  index: number;
  total: number;
  progress: ReturnType<typeof useScroll>["scrollYProgress"];
  title: string;
}> = ({ index, total, progress, title }) => {
  const T = Math.max(total - 1, 1);
  const activeAt = index / T;
  const halfStep = 0.5 / T;

  const opacity = useTransform(progress, (p) => {
    const dist = Math.abs(p - activeAt) / halfStep;
    return dist < 1 ? 1 : 0;
  });

  return <DotLabelText style={{ opacity }}>{title}</DotLabelText>;
};

/* ═══════════════════════════════════════════════════
   CLASSIFIED CARD (Personal Projects)
   ═══════════════════════════════════════════════════ */

const ClassifiedCard: React.FC<{
  project: PersonalProject;
  index: number;
}> = ({ project, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-5% 0px" });

  return (
    <ClWrap
      ref={ref}
      as={motion.article}
      $accent={project.accent}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.5,
        delay: (index % 3) * 0.08,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <ClAccent style={{ background: project.accent }} />
      <ClTitle>{project.title}</ClTitle>
      <ClDesc>{project.description}</ClDesc>
      <ClTechRow>
        {project.tech.map((t) => (
          <ClTech key={t} $accent={project.accent}>
            {t}
          </ClTech>
        ))}
      </ClTechRow>
      <ClLinks>
        <ClLink href={project.github} target="_blank" rel="noopener noreferrer">
          GitHub
        </ClLink>
        {project.live && (
          <ClLink
            href={project.live}
            target="_blank"
            rel="noopener noreferrer"
            $accent={project.accent}
          >
            Live &#8599;
          </ClLink>
        )}
      </ClLinks>
    </ClWrap>
  );
};

/* ═══════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════ */

const ProphetFooter: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <FooterWrap ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <PDivider>
          <PDivLine />
          <PDivDot>&#10040;</PDivDot>
          <PDivLine />
        </PDivider>
        <FooterRow>
          <div>
            <FooterLabel>What's Next?</FooterLabel>
            <FooterHeading>More spells in progress...</FooterHeading>
          </div>
          <FooterLink
            href="https://github.com/jugshaurya"
            target="_blank"
            rel="noopener noreferrer"
          >
            View all on GitHub &#8594;
          </FooterLink>
        </FooterRow>
        <FooterTagline>"Mischief Managed."</FooterTagline>
      </motion.div>
    </FooterWrap>
  );
};

/* ═══════════════════════════════════════════════════
   KEYFRAMES
   ═══════════════════════════════════════════════════ */

const shimmer = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const inkBleed = keyframes`
  0% { filter: blur(0px); }
  50% { filter: blur(0.3px); }
  100% { filter: blur(0px); }
`;

/* ═══════════════════════════════════════════════════
   STYLED — Page
   ═══════════════════════════════════════════════════ */

const Page = styled.section`
  background: #0a0810;
  position: relative;
  overflow: clip;
`;

/* ═══════════════════════════════════════════════════
   STYLED — Masthead (dark intro)
   ═══════════════════════════════════════════════════ */

const MastheadWrap = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
`;

const MastOrnament = styled.div`
  font-size: 0.7rem;
  color: #c4a050;
  letter-spacing: 1em;
  margin-bottom: 1.5rem;
  animation: ${shimmer} 3s ease-in-out infinite;
`;

const MastRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  @media (max-width: 700px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const MastEdition = styled.span`
  font-family: "Crimson Text", serif;
  font-size: 0.75rem;
  color: #7a6a50;
  letter-spacing: 0.15em;
  font-style: italic;
`;

const MastName = styled.h1`
  font-family: "Cinzel", serif;
  font-size: clamp(2.5rem, 8vw, 5.5rem);
  font-weight: 900;
  color: #e8d5a8;
  letter-spacing: 0.06em;
  line-height: 1;
  text-shadow: 0 0 60px rgba(196, 160, 80, 0.15), 0 2px 4px rgba(0, 0, 0, 0.5);
  animation: ${inkBleed} 4s ease-in-out infinite;
`;

const MastRule = styled.div`
  width: 100%;
  max-width: 700px;
  height: 2px;
  margin: 1rem auto;
  background: linear-gradient(
    90deg, transparent, #c4a05030, #c4a05060, #c4a05030, transparent
  );
`;

const MastMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: "Crimson Text", serif;
  font-size: 0.8rem;
  color: #8a7a60;
  margin: 0.8rem 0;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.3rem;
  }
`;

const MastDot = styled.span`
  color: #c4a050;
  font-size: 0.5rem;
  @media (max-width: 600px) { display: none; }
`;

const MastTagline = styled.p`
  font-family: "Caveat", cursive;
  font-size: 1.1rem;
  color: #c4a05080;
  margin: 0.5rem 0;
`;

const ScrollHint = motion.create(styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`);

const ScrollQuill = motion.create(styled.span`
  font-size: 1.2rem;
  color: #c4a050;
`);

const ScrollLbl = styled.span`
  font-family: "Crimson Text", serif;
  font-size: 0.65rem;
  color: #5a4a3a;
  letter-spacing: 0.15em;
`;

/* ═══════════════════════════════════════════════════
   STYLED — Carousel structure
   ═══════════════════════════════════════════════════ */

const CarouselOuter = styled.div`
  position: relative;
`;

const CarouselSticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SlideAbsolute = motion.create(styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;
  will-change: transform;
`);

const DotsCol = styled.div`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 20;
  align-items: flex-end;
  @media (max-width: 700px) {
    right: 0.4rem;
    gap: 0.35rem;
  }
`;

const DotRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const DotCircle = motion.create(styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c4a050;
  flex-shrink: 0;
`);

const DotLabelText = motion.create(styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.5rem;
  font-weight: 700;
  color: #c4a882;
  letter-spacing: 0.06em;
  white-space: nowrap;
  @media (max-width: 700px) {
    display: none;
  }
`);

/* ═══════════════════════════════════════════════════
   STYLED — Spell Book Page
   ═══════════════════════════════════════════════════ */

const SpellPage = styled.div`
  width: 100%;
  max-width: 960px;
  max-height: 85vh;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 30% 0%, #1a1428 0%, transparent 50%),
    radial-gradient(ellipse at 70% 100%, #0f0d1a 0%, transparent 50%),
    linear-gradient(135deg, #12101e 0%, #1a1428 50%, #0f0d1a 100%);
  color: #d4c8b0;
  padding: 2rem 2.5rem;
  position: relative;
  border: 2px solid #c4a05020;
  border-radius: 2px;
  box-shadow:
    0 0 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(196, 160, 80, 0.08),
    inset 0 0 80px rgba(0, 0, 0, 0.3);

  /* Parchment grain */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
      radial-gradient(ellipse at 80% 20%, rgba(196, 160, 80, 0.04) 0%, transparent 40%),
      radial-gradient(ellipse at 15% 85%, rgba(139, 105, 20, 0.03) 0%, transparent 35%);
    pointer-events: none;
  }

  /* Gold edge glow */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    box-shadow:
      inset 1px 0 0 rgba(196, 160, 80, 0.1),
      inset -1px 0 0 rgba(196, 160, 80, 0.1),
      inset 0 1px 0 rgba(196, 160, 80, 0.1),
      inset 0 -1px 0 rgba(196, 160, 80, 0.1);
    pointer-events: none;
  }

  & > * { position: relative; z-index: 1; }

  @media (max-width: 700px) {
    padding: 1.2rem 1rem;
  }
`;

const SpellNum = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #9a8a70;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
`;

const SpellClassBadge = styled.div<{ $side: string }>`
  display: inline-block;
  font-family: "Cinzel", serif;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 0.2rem 0.6rem;
  border-radius: 3px;
  margin-bottom: 0.8rem;
  color: ${({ $side }) => ($side === "recruiter" ? "#ffd700" : "#a0c8ff")};
  border: 1px solid ${({ $side }) => ($side === "recruiter" ? "rgba(255,215,0,0.3)" : "rgba(160,200,255,0.2)")};
  background: ${({ $side }) => ($side === "recruiter" ? "rgba(255,215,0,0.06)" : "rgba(160,200,255,0.04)")};
`;

const SpellName = styled.h2`
  font-family: "Cinzel", serif;
  font-size: clamp(1.3rem, 3vw, 2rem);
  font-weight: 900;
  color: #ffd700;
  letter-spacing: 0.04em;
  line-height: 1.15;
  margin-bottom: 0.2rem;
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.15);
`;

const SpellLatin = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.88rem;
  color: #b0a080;
  font-style: italic;
  margin-bottom: 0.6rem;
  line-height: 1.4;
`;

const SpellHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.3rem;
`;

const SpellTabs = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(196, 160, 80, 0.15);
  margin-bottom: 1rem;
`;

const SpellTab = styled.button<{ $active: boolean }>`
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.5rem 1rem;
  border: none;
  cursor: pointer;
  background: transparent;
  color: ${({ $active }) => ($active ? "#ffd700" : "#6a5a4a")};
  border-bottom: 2px solid ${({ $active }) => ($active ? "#ffd700" : "transparent")};
  transition: all 0.25s ease;
  &:hover { color: #ffd700; }
`;

const SpellTabContent = styled.div`
  flex: 1;
  min-height: 0;
`;

const SpellRule = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(196, 160, 80, 0.25), transparent);
  margin-bottom: 1rem;
  position: relative;
  &::after {
    content: "\\2726";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #12101e;
    padding: 0 0.6rem;
    font-size: 0.6rem;
    color: #8b6914;
  }
`;

const SpellIllustration = styled.div`
  margin-bottom: 1rem;
  border: 1px solid rgba(196, 160, 80, 0.15);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
  background: rgba(0, 0, 0, 0.2);
`;

const SpellImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 260px;
  object-fit: cover;
  object-position: top;
  display: block;
  filter: brightness(0.9) contrast(1.05);
  transition: filter 0.3s ease;
  ${SpellIllustration}:hover & {
    filter: brightness(1) contrast(1);
  }
`;

const SpellAnnotation = styled.div`
  position: absolute;
  bottom: 0.4rem;
  right: 0.6rem;
  font-family: "Caveat", cursive;
  font-size: 0.85rem;
  color: #ffd700;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
`;

const SpellBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 0.8rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const SpellSection = styled.div``;

const SpellLabel = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  font-weight: 700;
  color: #e0c070;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid rgba(196, 160, 80, 0.12);
`;

const SpellText = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.95rem;
  color: #e0d8c8;
  line-height: 1.75;
`;

const SpellStep = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 0.9rem;
  color: #d8d0c0;
  line-height: 1.7;
  margin-bottom: 0.3rem;
`;

const SpellStepNum = styled.span`
  font-family: "Cinzel", serif;
  font-weight: 700;
  color: #c4a050;
  margin-right: 0.2rem;
`;

const SpellPower = styled.div`
  background: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.1);
  border-radius: 3px;
  padding: 0.5rem 0.8rem;
  margin-bottom: 0.8rem;
`;

const SpellPowerText = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.8rem;
  color: #ffd700;
  letter-spacing: 0.03em;
`;

const SpellFooter = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(196, 160, 80, 0.12);
`;

const SpellIngredientsLabel = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.5rem;
  color: #9a8a70;
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

const SpellIngredients = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  flex: 1;
`;

const SpellIngredient = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.58rem;
  padding: 0.15rem 0.5rem;
  border: 1px solid rgba(196, 160, 80, 0.25);
  color: #e8e0d0;
  background: rgba(196, 160, 80, 0.06);
  border-radius: 2px;
  letter-spacing: 0.04em;
`;

const SpellLink = styled.a`
  font-family: "Cinzel", serif;
  font-size: 0.75rem;
  font-weight: 700;
  color: #ffd700;
  text-decoration: none;
  letter-spacing: 0.08em;
  transition: all 0.3s ease;
  &:hover { color: #fff; text-shadow: 0 0 8px rgba(255, 215, 0, 0.4); }
`;

const SpellVisit = styled.a`
  display:inline-flex; align-items:center; gap:0.35rem;
  margin:0.9rem 0 0.2rem;
  padding:0.5rem 1rem;
  border-radius:4px;
  font-family:"Cinzel",serif; font-size:0.68rem; font-weight:600;
  letter-spacing:0.12em; text-transform:uppercase; text-decoration:none;
  color:#2a1a0a;
  background:linear-gradient(160deg,#e8cd82,#c19a32);
  border:1px solid #e8cd82;
  cursor:none;
  transition:all 0.28s ease;

  &:hover{
    transform:translateY(-2px);
    box-shadow:0 6px 18px rgba(196,148,42,0.35);
  }
`;

const SpellByline = styled.div`
  font-family: "Crimson Text", serif;
  font-size: 0.6rem;
  color: #7a6a5a;
  text-align: center;
  margin-top: 0.6rem;
  font-style: italic;
  letter-spacing: 0.06em;
`;

/* ── Lightbox ── */
const LightboxOverlay = motion.create(styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(5, 3, 10, 0.92);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
`);

const LightboxClose = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.5rem;
  font-size: 2rem;
  color: #c4a882;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
  z-index: 1;
  transition: color 0.2s;
  &:hover { color: #ffd700; }
`;

const LightboxImg = motion.create(styled.img`
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  border: 3px solid rgba(196, 160, 80, 0.25);
  box-shadow: 0 0 60px rgba(0, 0, 0, 0.6);
  cursor: default;
`);

const LightboxCaption = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.8rem;
  color: #c4a882;
  margin-top: 1rem;
  letter-spacing: 0.1em;
`;

/* ═══════════════════════════════════════════════════
   STYLED — Personal Projects
   ═══════════════════════════════════════════════════ */

const PersonalSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 0;
`;

const PDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
`;

const PDivLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #c4a05025, transparent);
`;

const PDivDot = styled.span`
  color: #c4a05040;
  font-size: 0.6rem;
`;

const PSectionHead = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
`;

const PSectionLabel = styled.div`
  font-family: "Cinzel", serif;
  font-size: 0.55rem;
  color: #c4a05060;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  margin-bottom: 0.6rem;
`;

const PSectionTitle = styled.h2`
  font-family: "Cinzel", serif;
  font-size: clamp(1.3rem, 3vw, 2rem);
  font-weight: 700;
  color: #e8d5a8;
  letter-spacing: 0.04em;
  margin-bottom: 0.5rem;
`;

const PSectionSub = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.9rem;
  color: #8a7a60;
  font-style: italic;
`;

const ClassifiedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  border: 1px solid #c4a05012;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 550px) { grid-template-columns: 1fr; }
`;

const ClWrap = styled.article<{ $accent: string }>`
  position: relative;
  padding: 1.4rem;
  border: 1px solid #c4a05008;
  transition: all 0.3s ease;
  &:hover { background: rgba(196, 160, 80, 0.02); }
`;

const ClAccent = styled.div`
  width: 20px;
  height: 2px;
  margin-bottom: 0.8rem;
  opacity: 0.5;
`;

const ClTitle = styled.h4`
  font-family: "Playfair Display", serif;
  font-size: 1rem;
  font-weight: 700;
  color: #d8c898;
  margin-bottom: 0.4rem;
  line-height: 1.2;
`;

const ClDesc = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.8rem;
  color: #8a7a60;
  line-height: 1.6;
  margin-bottom: 0.6rem;
`;

const ClTechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.6rem;
`;

const ClTech = styled.span<{ $accent: string }>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  padding: 0.12rem 0.35rem;
  color: #6a5a4a;
  border: 1px solid ${({ $accent }) => $accent}15;
  letter-spacing: 0.03em;
  transition: border-color 0.3s ease;
  ${ClWrap}:hover & { border-color: ${({ $accent }) => $accent}30; }
`;

const ClLinks = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const ClLink = styled.a<{ $accent?: string }>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: ${({ $accent }) => $accent || "#6a5a4a"};
  text-decoration: none;
  letter-spacing: 0.05em;
  cursor: none;
  transition: color 0.3s ease;
  &:hover { color: #e8d5a8; }
`;

/* ═══════════════════════════════════════════════════
   STYLED — Footer
   ═══════════════════════════════════════════════════ */

const FooterWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 5rem;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem 0;
`;

const FooterLabel = styled.p`
  font-family: "Crimson Text", serif;
  font-size: 0.7rem;
  color: #5a4a3a;
  letter-spacing: 0.15em;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
`;

const FooterHeading = styled.h3`
  font-family: "Cinzel", serif;
  font-size: clamp(1.2rem, 2.5vw, 1.6rem);
  font-weight: 700;
  color: #e8d5a8;
`;

const FooterLink = styled.a`
  font-family: "Crimson Text", serif;
  font-size: 0.85rem;
  color: #c4a050;
  text-decoration: none;
  cursor: none;
  border-bottom: 1px solid #c4a05030;
  padding-bottom: 2px;
  transition: color 0.3s ease;
  &:hover { color: #e8d5a8; border-color: #e8d5a8; }
`;

const FooterTagline = styled.p`
  font-family: "Caveat", cursive;
  font-size: 1.2rem;
  color: #c4a05050;
  text-align: center;
  padding: 1rem 0;
`;
